import { create } from 'zustand';
import { Setup } from '../types';
import setupService from '../services/setup.service';

interface SetupState {
  // Cached active setup
  activeSetup: Setup | null;
  // Loading state
  isLoading: boolean;
  // Error state
  error: string | null;
  // Cache timestamp (for debugging/logging)
  cachedAt: number | null;

  // Load active setup (uses cache if available)
  loadActiveSetup: (forceRefresh?: boolean) => Promise<Setup | null>;
  // Clear cache (called when changing active setup)
  clearCache: () => void;
  // Set a new active setup and update cache
  setActiveSetup: (setupId: number) => Promise<void>;
}

export const useSetupStore = create<SetupState>((set, get) => ({
  activeSetup: null,
  isLoading: false,
  error: null,
  cachedAt: null,

  loadActiveSetup: async (forceRefresh = false) => {
    const state = get();

    // Return cached version if available and not forcing refresh
    if (!forceRefresh && state.activeSetup && state.cachedAt) {
      console.log('[SetupStore] Using cached active setup');
      return state.activeSetup;
    }

    set({ isLoading: true, error: null });

    try {
      console.log('[SetupStore] Fetching active setup from API');
      const setup = await setupService.getActive();

      if (setup) {
        set({
          activeSetup: setup,
          isLoading: false,
          cachedAt: Date.now(),
        });
        console.log('[SetupStore] Active setup cached:', setup.name);
      } else {
        set({
          activeSetup: null,
          isLoading: false,
          cachedAt: Date.now(),
        });
        console.log('[SetupStore] No active setup found');
      }

      return setup;
    } catch (error: any) {
      console.error('[SetupStore] Error loading active setup:', error);
      set({
        error: error.response?.data?.error || 'Erreur lors du chargement du setup',
        isLoading: false,
      });
      return null;
    }
  },

  clearCache: () => {
    console.log('[SetupStore] Cache cleared');
    set({
      activeSetup: null,
      cachedAt: null,
      error: null,
    });
  },

  setActiveSetup: async (setupId: number) => {
    set({ isLoading: true, error: null });

    try {
      // Call API to set active setup
      await setupService.setActive(setupId);

      // Clear cache to force reload with new active setup
      set({
        activeSetup: null,
        cachedAt: null,
        isLoading: false,
      });

      console.log('[SetupStore] Active setup changed, cache cleared');

      // Reload the new active setup
      await get().loadActiveSetup(true);
    } catch (error: any) {
      console.error('[SetupStore] Error setting active setup:', error);
      set({
        error: error.response?.data?.error || 'Erreur lors du changement de setup',
        isLoading: false,
      });
      throw error;
    }
  },
}));
